import { Injectable } from '@angular/core';

import {
  PracticeGenerationResult,
  PracticeQuestion,
} from '../interfaces/practice';

/**
 * Mémoire courte du dernier test d'entraînement composé.
 *
 * <p>Le moteur sait <em>pourquoi</em> chaque question a été tirée (lacune,
 * révision due, consolidation…), mais cette information n'existe qu'au moment
 * de la génération : le test enregistré, lui, ne la porte pas. Comme les
 * questions ne sont plus listées avant de commencer, c'est l'écran de passation
 * qui doit l'afficher — d'où ce relais entre le générateur et la session.</p>
 *
 * <p>Volontairement en mémoire seulement : après un rechargement de page la
 * justification disparaît, et la session s'affiche sans le panneau plutôt que
 * d'inventer une raison. Un test rouvert plus tard depuis l'historique n'a
 * jamais eu ce contexte ; ce n'est pas une régression, c'est le périmètre.</p>
 */
@Injectable({ providedIn: 'root' })
export class PracticeContextService {
  private testId: number | null = null;
  private seed: string | null = null;
  private byQuestion = new Map<number, PracticeQuestion>();

  /** Mémorise le tirage qui vient d'être composé, avant de lancer la session. */
  remember(result: PracticeGenerationResult): void {
    this.testId = result.testId;
    this.seed = result.seed ?? null;
    this.byQuestion = new Map(
      (result.questions ?? []).map((q) => [q.questionId, q]),
    );
  }

  /** Vrai si le contexte mémorisé concerne bien ce test. */
  covers(testId: number): boolean {
    return this.testId !== null && this.testId === Number(testId);
  }

  /** Justification d'une question, ou `null` hors contexte d'entraînement. */
  reasonFor(testId: number, questionId: number): PracticeQuestion | null {
    if (!this.covers(testId)) return null;
    return this.byQuestion.get(questionId) ?? null;
  }

  /** Graine du tirage — permet de reconstituer le test à l'identique. */
  seedFor(testId: number): string | null {
    return this.covers(testId) ? this.seed : null;
  }

  clear(): void {
    this.testId = null;
    this.seed = null;
    this.byQuestion = new Map();
  }
}
